import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/api";

// =======================
// HELPERS
// =======================

// normalize next url from DRF pagination
const normalizeNextUrl = (url) => {
  if (!url) return null;

  try {
    const parsed = new URL(url, window.location.origin);
    let path = parsed.pathname + parsed.search;

    if (path.startsWith("/api/")) {
      path = path.replace("/api", "");
    }

    return path;
  } catch {
    return url;
  }
};

// 🔥 remove empty params before request
const cleanParams = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([_, value]) =>
        value !== null && value !== "" && value !== undefined
    )
  );

// Ключ набору фільтрів — щоб знати, для яких фільтрів уже завантажено список
export const paramsKey = (params) => JSON.stringify(cleanParams(params));

// =======================
// INITIAL STATE
// =======================

const initialState = {
  // HOME
  products: [],
  next: null,
  // Для яких фільтрів зараз список products (paramsKey). Повернувшись на головну
  // з тими ж фільтрами, не перезавантажуємо каталог — і не втрачаємо прокрутку.
  loadedKey: null,

  banners: null, // null — ще не завантажені

  // ФІЛЬТРИ КАТАЛОГУ
  categories: [],
  brandsByCategory: [],
  modelsByBrand: [],

  currentProduct: null,

  // 🔥 FILTERS (ONLY USER-SELECTED)
  params: {
    price_min: null,
    price_max: null,
    ordering: null,
    search: null,
    model: null, // 🔥 NEW
  },

  status: "idle",
  error: null,
};

// =======================
// THUNKS
// =======================

// ---------- HOME ----------
// key — paramsKey першої сторінки; для наступних сторінок не передається
export const fetchProducts = createAsyncThunk(
  "products/fetchProducts",
  async ({ url = null }, { getState }) => {
    const { params } = getState().products;

    const requestUrl = url
      ? normalizeNextUrl(url)
      : "/products/";

    // Посилання next від DRF уже містить усі фільтри — не дописуємо їх удруге
    const response = await api.get(requestUrl, {
      params: url ? undefined : cleanParams(params),
    });

    return {
      results: response.data.results,
      next: response.data.next,
      initial: !url,
    };
  }
);

/**
 * Завантажити каталог для поточних фільтрів, якщо він ще не завантажений
 * (або вже не вантажиться) саме для них.
 */
export const loadHomeProducts = () => (dispatch, getState) => {
  const { params, loadedKey } = getState().products;
  const key = paramsKey(params);
  if (key === loadedKey) return;

  dispatch(resetHomeProducts(key));
  dispatch(fetchProducts({ key }));
};

export const fetchBanners = createAsyncThunk("products/fetchBanners", async () => {
  const response = await api.get("/banners/");
  return response.data.results || response.data || [];
});

// ---------- CATEGORIES ----------
export const fetchCategories = createAsyncThunk(
  "products/fetchCategories",
  async () => {
    const response = await api.get("/categories/");
    return response.data;
  }
);

// ---------- BRANDS BY CATEGORY ----------
export const fetchBrandsByCategory = createAsyncThunk(
  "products/fetchBrandsByCategory",
  async (slug) => {
    const response = await api.get("/brands/", {
      params: { category: slug },
    });

    return response.data.results ?? response.data;
  }
);

// ---------- 🔥 MODELS BY BRAND ----------
export const fetchModelsByBrand = createAsyncThunk(
  "products/fetchModelsByBrand",
  async (brandSlug) => {
    const response = await api.get("/models/", {
      params: { brand: brandSlug },
    });

    return response.data.results ?? response.data;
  }
);

// ---------- PRODUCT DETAIL ----------
export const fetchProduct = createAsyncThunk(
  "products/fetchProduct",
  async (slug) => {
    const response = await api.get(`/products/${slug}/`);
    return response.data;
  }
);

// =======================
// SLICE
// =======================

const productsSlice = createSlice({
  name: "products",
  initialState,

  reducers: {
    // 🔥 UPDATE FILTERS (ONLY WHAT USER SET)
    setParams: (state, action) => {
      state.params = {
        ...state.params,
        ...action.payload,
      };
    },

    // RESET HOME; payload — paramsKey, для якого зараз вантажимо список
    resetHomeProducts: (state, action) => {
      state.products = [];
      state.next = null;
      state.loadedKey = action.payload ?? null;
    },
  },

  extraReducers: (builder) => {
    builder
      // HOME
      .addCase(fetchProducts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload.initial) {
          // Відповідь на застарілі фільтри (користувач уже обрав інші) — ігноруємо
          if (action.meta.arg.key && action.meta.arg.key !== state.loadedKey) return;
          state.products = action.payload.results;
        } else {
          state.products.push(...action.payload.results);
        }
        state.next = action.payload.next;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = "failed";
        // Перша сторінка не завантажилась — наступний вхід на головну спробує знову
        if (action.meta.arg.key === state.loadedKey) state.loadedKey = null;
      })

      .addCase(fetchBanners.fulfilled, (state, action) => {
        state.banners = action.payload;
      })
      .addCase(fetchBanners.rejected, (state) => {
        state.banners = [];
      })

      // CATEGORIES
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })

      // BRANDS BY CATEGORY
      .addCase(fetchBrandsByCategory.fulfilled, (state, action) => {
        state.brandsByCategory = action.payload;
      })

      // 🔥 MODELS BY BRAND
      .addCase(fetchModelsByBrand.fulfilled, (state, action) => {
        state.modelsByBrand = action.payload;
      })

      // PRODUCT DETAIL
      .addCase(fetchProduct.pending, (state) => {
        state.status = "loading";
        state.currentProduct = null;
      })
      .addCase(fetchProduct.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.currentProduct = action.payload;
      });
  },
});

// =======================
// EXPORTS
// =======================

export const { setParams, resetHomeProducts } = productsSlice.actions;

export default productsSlice.reducer;
